<template>
  <div :class="wrapperClasses">
    <!-- Standard Label (non-floating) -->
    <label v-if="label && !floating" :for="inputId" class="input__label">
      {{ label }}
      <span v-if="required" class="input__required">*</span>
    </label>

    <div class="input__container">
      <span v-if="$slots.prepend || prependIcon" class="input__prepend">
        <slot name="prepend">
          <i v-if="prependIcon" :class="prependIcon"></i>
        </slot>
      </span>

      <input
        :id="inputId"
        ref="input"
        v-model="internalValue"
        :type="computedType"
        :placeholder="floating ? ' ' : placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :class="inputClasses"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
        @keyup="$emit('keyup', $event)"
      />

      <!-- Floating Label -->
      <label v-if="label && floating" :for="inputId" class="input__floating-label">
        {{ label }}
        <span v-if="required" class="input__required">*</span>
      </label>

      <!-- Character Counter -->
      <span v-if="maxlength && showCounter" class="input__counter">
        {{ currentLength }}/{{ maxlength }}
      </span>

      <!-- Password Toggle -->
      <span v-if="type === 'password'" class="input__toggle-password" @click="togglePassword">
        <svg v-if="showPassword" viewBox="0 0 24 24" width="18" height="18">
          <path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
        </svg>
        <svg v-else viewBox="0 0 24 24" width="18" height="18">
          <path fill="currentColor" d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
        </svg>
      </span>

      <span v-if="clearable && internalValue && !disabled" class="input__clear" @click="clearInput">
        <svg viewBox="0 0 24 24" width="18" height="18">
          <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </span>

      <span v-if="$slots.append || appendIcon" class="input__append">
        <slot name="append">
          <i v-if="appendIcon" :class="appendIcon"></i>
        </slot>
      </span>
    </div>

    <div v-if="hint && !error" class="input__hint">{{ hint }}</div>
    <div v-if="error && errorMessage" class="input__error">{{ errorMessage }}</div>
  </div>
</template>

<script>
let inputIdCounter = 0

export default {
  name: 'BaseInput',
  props: {
    modelValue: {
      type: [String, Number],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v)
    },
    floating: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    },
    prependIcon: {
      type: String,
      default: null
    },
    appendIcon: {
      type: String,
      default: null
    },
    maxlength: {
      type: [Number, String],
      default: null
    },
    showCounter: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'focus', 'blur', 'keydown', 'keyup', 'clear', 'enter'],
  data() {
    return {
      inputId: `input-${++inputIdCounter}`,
      internalValue: this.modelValue,
      isFocused: false,
      showPassword: false
    }
  },
  computed: {
    wrapperClasses() {
      return [
        'input-wrapper',
        `input-wrapper--${this.size}`,
        {
          'input-wrapper--floating': this.floating,
          'input-wrapper--disabled': this.disabled,
          'input-wrapper--error': this.error,
          'input-wrapper--focused': this.isFocused,
          'input-wrapper--has-value': !!this.internalValue
        }
      ]
    },
    inputClasses() {
      return [
        'input__field',
        {
          'input__field--has-prepend': this.$slots.prepend || this.prependIcon,
          'input__field--has-append': this.$slots.append || this.appendIcon || this.clearable || this.type === 'password'
        }
      ]
    },
    computedType() {
      if (this.type === 'password') {
        return this.showPassword ? 'text' : 'password'
      }
      return this.type
    },
    currentLength() {
      return this.internalValue ? String(this.internalValue).length : 0
    }
  },
  watch: {
    modelValue(newVal) {
      this.internalValue = newVal
    }
  },
  mounted() {
    if (this.autofocus) {
      this.$nextTick(() => {
        this.$refs.input?.focus()
      })
    }
  },
  methods: {
    onInput(e) {
      this.$emit('update:modelValue', e.target.value)
    },
    onFocus(e) {
      this.isFocused = true
      this.$emit('focus', e)
    },
    onBlur(e) {
      this.isFocused = false
      this.$emit('blur', e)
    },
    onKeydown(e) {
      this.$emit('keydown', e)
      if (e.key === 'Enter') {
        this.$emit('enter', e)
      }
    },
    clearInput() {
      this.internalValue = ''
      this.$emit('update:modelValue', '')
      this.$emit('clear')
      this.$refs.input?.focus()
    },
    togglePassword() {
      this.showPassword = !this.showPassword
    },
    focus() {
      this.$refs.input?.focus()
    },
    blur() {
      this.$refs.input?.blur()
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.input-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;

  // Sizes
  &--sm .input__container {
    height: $input-height-sm;
  }
  &--md .input__container {
    height: $input-height-md;
  }
  &--lg .input__container {
    height: $input-height-lg;
  }

  &--disabled {
    opacity: 0.6;
    .input__container {
      background-color: $color-gray-100;
      cursor: not-allowed;
    }
    .input__field {
      cursor: not-allowed;
    }
  }

  &--error .input__container {
    border-color: $color-danger;
  }

  &--error.input-wrapper--focused .input__container {
    box-shadow: 0 0 0 3px rgba($color-danger, 0.15);
  }

  &--focused .input__container {
    border-color: $color-primary;
    box-shadow: 0 0 0 3px $input-focus-ring-color;
  }
}

.input__label {
  display: block;
  margin-bottom: $spacing-2;
  font-size: $font-size-sm;
  font-weight: $font-weight-medium;
  color: $color-text-primary;
}

.input__required {
  color: $color-danger;
  margin-left: 2px;
}

.input__container {
  display: flex;
  align-items: center;
  position: relative;
  background-color: $color-bg-primary;
  border: $input-border-width solid $input-border-color;
  border-radius: $input-border-radius;
  transition: border-color $transition-fast $transition-timing,
              box-shadow $transition-fast $transition-timing;
  overflow: hidden;

  &:hover:not(:focus-within) {
    border-color: $color-border-dark;
  }
}

.input__field {
  flex: 1;
  width: 100%;
  height: 100%;
  padding: $input-padding-md;
  border: none;
  outline: none;
  background: transparent;
  font-family: $font-family-base;
  font-size: $font-size-sm;
  color: $color-text-primary;

  &::placeholder {
    color: $color-text-tertiary;
  }

  &--has-prepend {
    padding-left: 0;
  }

  &--has-append {
    padding-right: 0;
  }
}

.input__prepend,
.input__append {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  color: $color-text-secondary;
}

.input__clear,
.input__toggle-password {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  color: $color-text-tertiary;
  cursor: pointer;
  transition: color $transition-fast;

  &:hover {
    color: $color-text-secondary;
  }
}

.input__counter {
  padding: 0 12px 0 4px;
  font-size: $font-size-xs;
  color: $color-text-tertiary;
  font-weight: $font-weight-medium;
}

.input__hint {
  margin-top: $spacing-1;
  font-size: $font-size-xs;
  color: $color-text-tertiary;
}

.input__error {
  margin-top: $spacing-1;
  font-size: $font-size-xs;
  color: $color-danger;
}

// ===============================
// Floating Label Styles
// ===============================
.input-wrapper--floating {
  .input__container {
    position: relative;
  }

  .input__field {
    padding-top: 14px;
    padding-bottom: 2px;

    &::placeholder {
      opacity: 0;
      transition: opacity $transition-normal;
    }
  }

  .input__floating-label {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: $color-text-tertiary;
    font-size: $font-size-sm;
    font-weight: $font-weight-normal;
    pointer-events: none;
    transition: all $transition-normal $transition-timing;
    background: transparent;
    padding: 0 4px;
    line-height: 1;
    z-index: 1;
  }

  &.input-wrapper--focused,
  &.input-wrapper--has-value {
    .input__field::placeholder {
      opacity: 1;
    }

    .input__floating-label {
      top: 0;
      transform: translateY(-50%);
      font-size: 11px;
      font-weight: $font-weight-medium;
      color: $color-primary;
      background: $color-bg-primary;
    }
  }

  &.input-wrapper--has-value:not(.input-wrapper--focused) {
    .input__floating-label {
      color: $color-text-secondary;
    }
  }

  &.input-wrapper--error {
    .input__floating-label {
      color: $color-danger;
    }
  }

  // Adjust for prepend icon
  &.input-wrapper--has-prepend {
    .input__floating-label {
      left: 44px;
    }

    &.input-wrapper--focused,
    &.input-wrapper--has-value {
      .input__floating-label {
        left: 14px;
      }
    }
  }
}

// Responsive - prevent zoom on iOS
@media (max-width: $breakpoint-sm) {
  .input__field {
    font-size: 16px;
  }
}
</style>
